import React, { useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, PhoneIcon, MailIcon, DownloadIcon, FileSpreadsheetIcon, ShieldIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { Insurance as InsuranceType } from '../../types';
import { exportInsurancesToExcel } from '../../utils/excelExport';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../ui/Pagination';
export function Insurance() {
  const {
    insurances,
    addInsurance,
    updateInsurance,
    deleteInsurance
  } = useData();
  const {
    user
  } = useAuth();
  const isAdmin = user?.role === 'administrador';
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedInsurance, setSelectedInsurance] = useState<InsuranceType | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    coverageType: '',
    phone: '',
    email: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isExporting, setIsExporting] = useState(false);
  const filteredInsurances = insurances.filter(i => i.name.toLowerCase().includes(searchTerm.toLowerCase()) || i.company.toLowerCase().includes(searchTerm.toLowerCase()));
  const {
    paginatedItems: paginatedInsurances,
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    goToPage,
    nextPage,
    prevPage
  } = usePagination(filteredInsurances);
  const openNewForm = () => {
    setSelectedInsurance(null);
    setFormData({
      name: '',
      company: '',
      coverageType: '',
      phone: '',
      email: ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (insurance: InsuranceType) => {
    setSelectedInsurance(insurance);
    setFormData({
      name: insurance.name,
      company: insurance.company,
      coverageType: insurance.coverageType,
      phone: insurance.phone,
      email: insurance.email
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openDeleteConfirm = (insurance: InsuranceType) => {
    setSelectedInsurance(insurance);
    setIsDeleteConfirmOpen(true);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.name) errors.name = 'El nombre es requerido';
    if (!formData.company) errors.company = 'La compañía es requerida';
    if (!formData.coverageType) errors.coverageType = 'El tipo de cobertura es requerido';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    if (selectedInsurance) {
      updateInsurance(selectedInsurance.id, formData);
    } else {
      addInsurance(formData);
    }
    setIsFormOpen(false);
  };
  const handleDelete = () => {
    if (selectedInsurance) {
      deleteInsurance(selectedInsurance.id);
      setIsDeleteConfirmOpen(false);
    }
  };
  const handleExportToExcel = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportInsurancesToExcel(insurances);
    setIsExporting(false);
  };
  return <div className="space-y-6" data-id="element-1258">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-1259">
        <div data-id="element-1260">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1261">
            Seguros
          </h1>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-1262">
            Gestión de seguros médicos
          </p>
        </div>
        <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1264" />} onClick={openNewForm} data-id="element-1263">
          Nuevo Seguro
        </Button>
      </div>

      {/* Beautiful Export Card - Only for Admin */}
      {isAdmin && <Card className="bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-950/30 dark:to-cyan-950/30 border-teal-200 dark:border-teal-800" data-id="element-1265">
          <div className="flex items-center justify-between" data-id="element-1266">
            <div className="flex items-center gap-4" data-id="element-1267">
              <div className="w-14 h-14 rounded-xl bg-teal-500 dark:bg-teal-600 flex items-center justify-center shadow-lg shadow-teal-500/20" data-id="element-1268">
                <FileSpreadsheetIcon className="w-7 h-7 text-white" data-id="element-1269" />
              </div>
              <div data-id="element-1270">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1" data-id="element-1271">
                  Exportar Base de Datos
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1272">
                  Descarga todos los seguros en formato Excel
                </p>
                <div className="flex items-center gap-4 mt-2" data-id="element-1273">
                  <div className="flex items-center gap-1.5 text-sm text-teal-700 dark:text-teal-400" data-id="element-1274">
                    <ShieldIcon className="w-4 h-4" data-id="element-1275" />
                    <span className="font-medium" data-id="element-1276">
                      {insurances.length} seguros
                    </span>
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1277">
                    • Incluye: Nombre, Compañía, Cobertura, Contacto
                  </div>
                </div>
              </div>
            </div>
            <Button leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-1279" />} onClick={handleExportToExcel} isLoading={isExporting} className="shadow-lg shadow-teal-500/20 hover:shadow-xl hover:shadow-teal-500/30 transition-all duration-200" data-id="element-1278">
              {isExporting ? 'Exportando...' : 'Descargar Excel'}
            </Button>
          </div>
        </Card>}

      {/* Search */}
      <Card data-id="element-1280">
        <div className="relative" data-id="element-1281">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1282" />
          <Input placeholder="Buscar seguros..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-id="element-1283" />
        </div>
      </Card>

      {/* Insurance List */}
      <Card padding="none" data-id="element-1284">
        <div className="overflow-x-auto" data-id="element-1285">
          <table className="w-full" data-id="element-1286">
            <thead data-id="element-1287">
              <tr className="border-b border-gray-200 dark:border-gray-700" data-id="element-1288">
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1289">
                  Nombre
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1290">
                  Compañía
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1291">
                  Cobertura
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1292">
                  Contacto
                </th>
                <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1293">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-1294">
              {filteredInsurances.length === 0 ? <tr data-id="element-1295">
                  <td colSpan={5} className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-1296">
                    No se encontraron seguros
                  </td>
                </tr> : paginatedInsurances.map(insurance => <tr key={insurance.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50" data-id="element-1297">
                    <td className="py-4 px-6" data-id="element-1298">
                      <p className="font-medium text-gray-900 dark:text-white" data-id="element-1299">
                        {insurance.name}
                      </p>
                    </td>
                    <td className="py-4 px-6 text-gray-900 dark:text-gray-100" data-id="element-1300">
                      {insurance.company}
                    </td>
                    <td className="py-4 px-6" data-id="element-1301">
                      <Badge variant="info" data-id="element-1302">{insurance.coverageType}</Badge>
                    </td>
                    <td className="py-4 px-6" data-id="element-1303">
                      <div className="space-y-1" data-id="element-1304">
                        {insurance.phone && <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1" data-id="element-1305">
                            <PhoneIcon className="w-3 h-3" data-id="element-1306" /> {insurance.phone}
                          </p>}
                        {insurance.email && <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1" data-id="element-1307">
                            <MailIcon className="w-3 h-3" data-id="element-1308" /> {insurance.email}
                          </p>}
                      </div>
                    </td>
                    <td className="py-4 px-6" data-id="element-1309">
                      <div className="flex items-center justify-end gap-2" data-id="element-1310">
                        <button onClick={() => openEditForm(insurance)} className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors" data-id="element-1311">
                          <EditIcon className="w-4 h-4" data-id="element-1312" />
                        </button>
                        <button onClick={() => openDeleteConfirm(insurance)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" data-id="element-1313">
                          <TrashIcon className="w-4 h-4" data-id="element-1314" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="px-6 pb-4" data-id="element-1315">
          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} onPageChange={goToPage} onNext={nextPage} onPrev={prevPage} data-id="element-1316" />
        </div>
      </Card>

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedInsurance ? 'Editar Seguro' : 'Nuevo Seguro'} size="md" icon={<ShieldIcon className="w-5 h-5" data-id="element-1318" />} accentColor="sky" data-id="element-1317">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1319">
          {/* Insurance Info */}
          <div data-id="element-1320">
            <div className="flex items-center gap-2 mb-4" data-id="element-1321">
              <span className="w-1.5 h-5 rounded-full bg-sky-500" data-id="element-1322"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-1323">
                Información del Seguro
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-1324">
              <Input label="Nombre del Seguro" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} error={formErrors.name} placeholder="Ej: ARS Humano" data-id="element-1325" />
              <div className="grid grid-cols-2 gap-4" data-id="element-1326">
                <Input label="Compañía" value={formData.company} onChange={e => setFormData({
                ...formData,
                company: e.target.value
              })} error={formErrors.company} placeholder="Ej: Humano Seguros" data-id="element-1327" />
                <Input label="Tipo de Cobertura" value={formData.coverageType} onChange={e => setFormData({
                ...formData,
                coverageType: e.target.value
              })} error={formErrors.coverageType} placeholder="Ej: Completo, Básico" data-id="element-1328" />
              </div>
            </div>
          </div>

          {/* Contact */}
          <div data-id="element-1329">
            <div className="flex items-center gap-2 mb-4" data-id="element-1330">
              <span className="w-1.5 h-5 rounded-full bg-cyan-500" data-id="element-1331"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-1332">
                Contacto
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-1333">
              <div className="grid grid-cols-2 gap-4" data-id="element-1334">
                <Input label="Teléfono" value={formData.phone} onChange={e => setFormData({
                ...formData,
                phone: e.target.value
              })} placeholder="809-555-0000" data-id="element-1335" />
                <Input type="email" label="Correo Electrónico" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} placeholder="info@seguro.com" data-id="element-1336" />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-1337">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-1338">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-1339">
              {selectedInsurance ? 'Guardar Cambios' : 'Crear Seguro'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} title="Confirmar Eliminación" size="sm" data-id="element-1340">
        <div className="space-y-4" data-id="element-1341">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-1342">
            ¿Está seguro de que desea eliminar el seguro "
            {selectedInsurance?.name}"?
          </p>
          <div className="flex justify-end gap-3" data-id="element-1343">
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)} data-id="element-1344">
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} data-id="element-1345">
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}